import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth, useSignIn } from "@clerk/tanstack-react-start";
import { useCallback, useEffect, useRef } from "react";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import { normalizeAppPath } from "~/lib/security";

const searchSchema = z.object({
	redirect: z.string().optional(),
});

export const Route = createFileRoute("/sign-in")({
	validateSearch: (search) => {
		const parsed = searchSchema.parse(search);
		return parsed.redirect === undefined
			? {}
			: { redirect: normalizeAppPath(parsed.redirect) };
	},
	component: SignInPage,
});

function SignInPage() {
	const navigate = useNavigate();
	const { isLoaded, isSignedIn } = useAuth();
	const { signIn } = useSignIn();
	const { redirect } = Route.useSearch();
	const startedRef = useRef(false);

	const finalRedirect = redirect ?? "/dashboard";

	const authenticate = useCallback(async () => {
		if (!isLoaded || isSignedIn || !signIn || startedRef.current) return;

		startedRef.current = true;

		try {
			await signIn.sso({
				strategy: "oauth_discord",
				redirectCallbackUrl: `${window.location.origin}/sso-callback?redirect=${encodeURIComponent(finalRedirect)}`,
				redirectUrl: new URL(
					finalRedirect,
					window.location.origin,
				).toString(),
			});
		} catch (error) {
			startedRef.current = false;
			console.error("Failed to start SSO:", error);
		}
	}, [isLoaded, isSignedIn, signIn, finalRedirect]);

	useEffect(() => {
		if (!isLoaded) return;

		if (isSignedIn) {
			navigate({ to: finalRedirect });
			return;
		}

		authenticate();
	}, [isLoaded, isSignedIn, navigate, finalRedirect, authenticate]);

	return (
		<div className="flex min-h-screen items-center justify-center">
			<div className="flex flex-col items-center gap-4 text-center">
				<img
					src="/icon-256.png"
					alt="Overseer"
					className="h-12 w-12 rounded-xl"
				/>
				<p className="text-sm text-muted-foreground">
					{isLoaded && isSignedIn
						? "Already signed in, redirecting…"
						: "Redirecting to Discord…"}
				</p>
				{(!isLoaded || !isSignedIn) && (
					<p className="text-xs text-muted-foreground">
						You should be redirected automatically. If not, click{" "}
						<Button
							variant="link"
							className="h-auto p-0"
							onClick={authenticate}
						>
							here
						</Button>
						.
					</p>
				)}
			</div>
		</div>
	);
}
